import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
// Import the new component
import CompetitorIntroductionGrid from './CompetitorIntroductionGrid'
import RacingCompetitor from './RacingCompetitor'
import { Countdown } from './Announcements'
import WavyGrass from '../../../../assets/wavy-grass.png'
import raceStartSound from '../../../../assets/horse_race_gate_open_start.mp3'
import raceStartGallopingSound from '../../../../assets/horse_race_start_galloping.mp3'
import crowdCheeringSound from '../../../../assets/crowd_cheering_loop.mp3'
import {
  shuffleCompetitorPositions,
  type CompetitorForRandomization
} from '../../../../utils/positionRandomizer'
import {
  generateRacingPatterns,
  RacingPattern,
  PatternGenerationConfig
} from '../../../../utils/racingPatternGenerator'
import { clearAllRaceCaches, PerformanceMonitor } from '../../../../utils/performanceUtils'

// Define interfaces for the component props
interface Competitor {
  id: string
  name: string
  image: string
  points: number
  bugs: number
  role?: string
  coins?: number
  events?: Array<{
    type: string
    coins: number
    positionTo: number
    time: number
  }>
  /* Allow additional dynamic properties used by other Winner components
     (avoids “index signature” compatibility errors between different
     Competitor definitions across the code-base). */
  [key: string]: unknown
}

interface CompetitorStyles {
  [competitorId: string]: {
    borderColor?: string
    rgbValues?: number[]
  }
}

interface LanePosition {
  laneY: number
  horseY: number
}

interface RaceTrackProps {
  competitors: Competitor[]
  introducedCount: number
  raceStarted: boolean
  winner: Competitor | null
  onRaceFinished: () => void
  startingPositionX: number
  onIntroductionsComplete: () => void
  competitorStyles: CompetitorStyles
}

// Define race phases as a union type
type Phase = 'intro' | 'introducing' | 'lining-up' | 'countdown' | 'racing'

const RaceTrack: React.FC<RaceTrackProps> = ({
  competitors,
  introducedCount,
  raceStarted,
  winner,
  onRaceFinished,
  startingPositionX,
  onIntroductionsComplete,
  competitorStyles
}) => {
  const containerHeight = 1100
  const containerWidth = 1200
  const finishLineX = containerWidth * 0.9

  // Race phases: "intro" → "introducing" → "lining-up" → "countdown" → "racing"
  const [phase, setPhase] = useState<Phase>("intro")
  // Removed unused currentIntroIndex state
  const [finishedCompetitors, setFinishedCompetitors] = useState<string[]>([])
  const [isRacingActive, setIsRacingActive] = useState<boolean>(false)
  const crowdAudioRef = useRef<HTMLAudioElement | null>(null)

  // Position randomization state
  const [randomizedCompetitors, setRandomizedCompetitors] = useState<Competitor[]>([])

  // Racing patterns state
  const [racingPatterns, setRacingPatterns] = useState<RacingPattern[]>([])
  const [patternConfig] = useState<PatternGenerationConfig>({
    totalRaceTime: 8000, // 8 seconds
    minSpeedVariation: 0.6,
    maxSpeedVariation: 1.8,
    comebackProbability: 0.4,
    finalBurstProbability: 0.5,
    segmentCount: 4
  })

  // Active competitors (memoized to prevent re-renders)
  const activeCompetitors = useMemo<Competitor[]>(() => {
    return competitors.slice(0, introducedCount)
  }, [competitors, introducedCount])

  // Randomized competitors for race display (use randomized order during race phases)
  const displayCompetitors = useMemo<Competitor[]>(() => {
    // During introduction phase, use original order
    if (phase === "intro" || phase === "introducing") {
      return activeCompetitors
    }
    // During race phases, use randomized order if available
    return randomizedCompetitors.length > 0 ? randomizedCompetitors : activeCompetitors
  }, [activeCompetitors, randomizedCompetitors, phase])

  // Dynamic layout calculation based on competitor count - REFACTORED
  const { lanePositions } = useMemo<{ lanePositions: LanePosition[] }>(() => {
    const containerHeight = 1100
    const topMinimum = 300 // Increased minimum distance from top for the banner
    const bottomBuffer = 60 // Buffer space at the bottom
    const availableHeight = containerHeight - topMinimum - bottomBuffer

    const competitorCount = activeCompetitors?.length || 0
    if (competitorCount === 0) {
      return { lanePositions: [] }
    }

    // If only one competitor, place in the middle of available space
    if (competitorCount === 1) {
      return {
        lanePositions: [{
          laneY: topMinimum + (availableHeight / 2),
          horseY: topMinimum + (availableHeight / 2) - 30 // Adjusted for horse position (moved down 60px)
        }]
      }
    }

    // For multiple competitors, distribute evenly
    const gap = availableHeight / (competitorCount - 1)
    const positions: LanePosition[] = []

    for (let i = 0; i < competitorCount; i++) {
      // Calculate from bottom up: bottomBuffer + (gap * reverseIndex)
      const reverseIndex = (competitorCount - 1) - i
      const laneY = containerHeight - bottomBuffer - (gap * reverseIndex)
      const horseY = laneY - 10 // Reduced offset to move horse down 60px

      positions.push({ laneY, horseY })
    }

    return { lanePositions: positions }
  }, [activeCompetitors?.length])

  // Initialize position randomization and racing patterns when competitors are ready
  useEffect(() => {
    if (activeCompetitors.length > 0 && phase === "intro") {
      // Cast competitors to the randomization interface
      const competitorsForRandomization: CompetitorForRandomization[] = activeCompetitors.map(comp => ({
        ...comp, // Include all properties (id and name are already included)
      }))

      // Shuffle competitor positions
      const { shuffledCompetitors } = shuffleCompetitorPositions(
        competitorsForRandomization,
        lanePositions,
        winner?.id
      )

      // Cast back to Competitor type and update state
      const randomizedComps = shuffledCompetitors as Competitor[]
      setRandomizedCompetitors(randomizedComps)

      // Generate racing patterns for the randomized competitors (with performance monitoring)
      const competitorIds = randomizedComps.map(comp => comp.id)

      // Prepare competitor data for suspense calculation
      const competitorData = randomizedComps.map(comp => ({
        id: comp.id,
        points: comp.points,
        bugs: comp.bugs
      }))

      // Monitor pattern generation performance
      const startTime = PerformanceMonitor.startMeasurement('pattern-generation')
      const patterns = generateRacingPatterns(competitorIds, winner?.id, patternConfig, competitorData)
      PerformanceMonitor.endMeasurement('pattern-generation', startTime)

      setRacingPatterns(patterns)
    }
  }, [activeCompetitors, lanePositions, winner?.id, phase, patternConfig])

  // Generate random offsets once and reuse them - use display competitors for consistency
  const grassOffsets = useMemo<number[]>(() => {
    return Array(displayCompetitors.length).fill(0).map(() =>
      Math.floor(Math.random() * 141) // Random offset between 0-40px
    )
  }, [displayCompetitors.length]) // Only regenerate if number of display competitors changes

  // CALLBACKS - Define all callbacks before they're used in effects

  // Function to fade out and stop crowd audio (move this BEFORE handleCompetitorFinish)
  const fadeOutAndStopCrowdAudio = useCallback((): void => {
    if (crowdAudioRef.current) {
      const audio = crowdAudioRef.current
      const fadeOutDuration = 300 // ms
      const fadeSteps = 20 // number of steps in fade
      const initialVolume = audio.volume
      const volumeStep = initialVolume / fadeSteps

      let currentStep = 0
      const fadeInterval = setInterval(() => {
        currentStep++
        const newVolume = initialVolume - (volumeStep * currentStep)

        if (newVolume <= 0 || currentStep >= fadeSteps) {
          audio.pause()
          audio.currentTime = 0
          clearInterval(fadeInterval)
        } else {
          audio.volume = newVolume
        }
      }, fadeOutDuration / fadeSteps)
    }
  }, [])

  // Handle competitor finishing race (moved after fadeOutAndStopCrowdAudio)
  const handleCompetitorFinish = useCallback((competitorId: string, isWinnerCrossingFinish: boolean = false): void => {

    setFinishedCompetitors(prev => {
      // Skip if already finished
      if (prev.includes(competitorId)) {
        return prev
      }

      const newFinished = [...prev, competitorId]

      // If this is the winner crossing the finish line, trigger race completion
      if (isWinnerCrossingFinish) {

        setTimeout(() => {
          fadeOutAndStopCrowdAudio()
          onRaceFinished()
        }, 1500)
      }
      // If all competitors have finished but no winner signal yet
      else if (newFinished.length === activeCompetitors.length) {
        // Check if winner is in the finished list
        if (winner && newFinished.includes(winner.id)) {
          setTimeout(() => {
            fadeOutAndStopCrowdAudio()
            onRaceFinished()
          }, 1500)
        }
      }

      return newFinished
    })
  }, [winner, activeCompetitors.length, onRaceFinished, fadeOutAndStopCrowdAudio])

  // Handle countdown completion
  const handleCountdownComplete = useCallback((): void => {
    // Introduce a small delay to ensure countdown exit animation completes
    setTimeout(() => {
      // Play the race start sound
      const audio = new Audio(raceStartSound)
      const audioGalloping = new Audio(raceStartGallopingSound)
      audioGalloping.volume = 0.5 // Set volume for galloping sound
      audio.play().catch(error => {
        console.warn("Could not play race start sound:", error)
      })
      audioGalloping.play().catch(error => {
        console.warn("Could not play galloping sound:", error)
      })

      // Play crowd cheering sound on loop
      if (!crowdAudioRef.current) {
        crowdAudioRef.current = new Audio(crowdCheeringSound)
        crowdAudioRef.current.loop = true
        crowdAudioRef.current.volume = 0.7 // Set to appropriate level
      }
      crowdAudioRef.current.play().catch(error => {
        console.warn("Could not play crowd cheering sound:", error)
      })

      setPhase("racing")
      setIsRacingActive(true)
    }, 300) // Just enough time for the countdown exit animation
  }, [])

  // Handle competitor introduction completion
  const handleIntroductionComplete = useCallback((): void => {
    // All introductions complete, move to line-up phase
    setPhase("lining-up")
    if (onIntroductionsComplete) {
      onIntroductionsComplete()
    }
  }, [onIntroductionsComplete])

  // EFFECTS - Now use the callbacks in effects

  // Initialize component and start introduction sequence
  useEffect(() => {
    // Reset everything first
    setPhase("intro")
    setFinishedCompetitors([])
    setIsRacingActive(false)

    // Important: Only start if we have competitors
    if (activeCompetitors.length > 0) {
      // Start the introduction sequence after a brief delay
      const timer: NodeJS.Timeout = setTimeout(() => {
        setPhase("introducing")
      }, 500)

      return () => clearTimeout(timer)
    }

    // No extra clean-up required when there are no competitors.
    return () => { /* noop */ }
  }, [activeCompetitors.length]) // Depend on activeCompetitors.length to re-trigger on changes

  // Handle race start trigger (from parent)
  useEffect(() => {
    if (raceStarted && phase === "lining-up") {
      setPhase("countdown")
    }
    // Added return statement
    return () => { }
  }, [raceStarted, phase])

  // Safety timer to ensure race always completes
  useEffect(() => {
    if (phase === "racing") {
      const safetyTimer: NodeJS.Timeout = setTimeout(() => {
        // If we've been racing for 30 seconds, force finish
        if (phase === "racing") {
          // Force any unfinished competitors to be added
          const missingCompetitors = activeCompetitors.filter(
            comp => !finishedCompetitors.includes(comp.id)
          )

          if (missingCompetitors.length > 0) {
            missingCompetitors.forEach(comp => {
              handleCompetitorFinish(comp.id)
            })
          }
        }
      }, 30000) // 30 seconds should be plenty of time

      return () => clearTimeout(safetyTimer)
    }
    // Added return statement
    return () => { }
  }, [phase, activeCompetitors, finishedCompetitors, handleCompetitorFinish])

  // Check if race is finished
  useEffect(() => {
    if (phase === "racing" && finishedCompetitors.length === activeCompetitors.length && activeCompetitors.length > 0) {
      // Notify parent after a delay
      const timer: NodeJS.Timeout = setTimeout(() => {
        // Just before finishing, log details for debugging
        fadeOutAndStopCrowdAudio()
        onRaceFinished()
      }, 1500)
      return () => clearTimeout(timer)
    }
    // Added return statement
    return () => { }
  }, [finishedCompetitors, activeCompetitors, phase, onRaceFinished, winner, fadeOutAndStopCrowdAudio])

  // Cleanup audio and caches when component unmounts
  useEffect(() => {
    return () => {
      if (crowdAudioRef.current) {
        crowdAudioRef.current.pause()
        crowdAudioRef.current = null
      }

      // Clear all race-related caches for memory management
      clearAllRaceCaches()

      // Clear performance measurements in development
      if (process.env.NODE_ENV === 'development') {
        PerformanceMonitor.clearMeasurements()
      }
    }
  }, [])

  return (
    <div className="relative overflow-hidden" style={{ height: containerHeight, width: containerWidth }}>
      {/* Race background (track) */}
      <div className="absolute inset-0">
        {/* Track lanes */}
        {(phase === "lining-up" || phase === "countdown" || phase === "racing") &&
          displayCompetitors.map((_, index) => {
            // Use the calculated position from lanePositions
            const yPos = lanePositions[index]?.laneY
            // Each lane has a z-index based on its row - each row gets +10 to z-index
            const laneZIndex = 10 + (index * 10) + 2
            // Use the pre-calculated offset instead of generating a new one
            const randomXOffset = grassOffsets[index]

            return (
              <div
                key={`lane-${index}`}
                className="absolute"
                style={{
                  left: 0,
                  top: yPos,
                  width: '100%',
                  height: '71px',
                  zIndex: laneZIndex,
                  backgroundImage: `url(${WavyGrass})`,
                  backgroundSize: 'auto 100%',
                  backgroundRepeat: 'repeat-x',
                  backgroundPosition: `${randomXOffset}px 0`,
                  transform: 'translateY(-12px)',
                  overflow: 'hidden'
                }}
              >
              </div>
            )
          })
        }
        {/* Finish line - positioned with proper z-index */}
        {(phase === "lining-up" || phase === "countdown" || phase === "racing") && (
          <div
            className="absolute rounded-2xl"
            style={{
              left: finishLineX + 70,
              top: 115,
              width: '60px',
              height: containerHeight,
              zIndex: 5, // Below the lanes and horses
              background: 'repeating-conic-gradient(#499a2c 0% 25%, #a6f794 0% 50%) 50% / 20px 20px',
              backgroundAttachment: 'fixed'
            }}
          />
        )}
      </div>
      {/* COUNTDOWN - using AnimatePresence to properly handle mounting/unmounting */}
      <AnimatePresence mode="wait">
        {phase === "countdown" && (
          <motion.div
            key="race-countdown"
            className="absolute z-50 flex items-center justify-center"
            style={{
              left: 0,
              right: 0,
              top: 0,
              bottom: 0
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Countdown onComplete={handleCountdownComplete} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Replace the INTRODUCTION PHASE section */}
      <AnimatePresence mode="wait">
        {phase === "introducing" && activeCompetitors.length > 0 && (
          <CompetitorIntroductionGrid
            key="competitor-grid"
            competitors={activeCompetitors}
            onAnimationComplete={handleIntroductionComplete}
            competitorStyles={competitorStyles}
          />
        )}
      </AnimatePresence>

      {/* RACING PHASE - competitors visible during lining-up, countdown, racing phases */}
      <AnimatePresence>
        {(phase === "lining-up" || phase === "countdown" || phase === "racing") && (
          <>
            {displayCompetitors.map((competitor, index) => {
              const yPos = lanePositions[index]?.horseY
              const style = competitorStyles[competitor.id]
              // Horse is above its lane but below the next lane
              const competitorZIndex = 10 + (index * 10)

              // Find the racing pattern for this competitor
              const racingPattern = racingPatterns.find(pattern => pattern.competitorId === competitor.id)

              return (
                <RacingCompetitor
                  key={`racer-${competitor.id}`}
                  competitor={competitor}
                  yPos={yPos}
                  startX={startingPositionX}
                  finishLineX={finishLineX}
                  isLiningUp={phase === "lining-up" || phase === "countdown"}
                  isRacing={phase === "racing" && isRacingActive}
                  winner={!!(winner && winner.id === competitor.id)}
                  entryDelay={index * 0.2}
                  onFinish={(isWinner: boolean) => handleCompetitorFinish(competitor.id, isWinner)}
                  borderColor={style.borderColor}
                  horseRgbValues={style.rgbValues} // Pass the RGB values
                  zIndex={competitorZIndex}
                  racingPattern={racingPattern} // Pass the racing pattern (will be used in task 4)
                />
              )
            })}
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default RaceTrack
